import { useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { submitSuggestion } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lightbulb, AlertTriangle, ShieldAlert, CheckCircle, Loader2 } from 'lucide-react';

type SuggestionType = 'Suggestion' | 'Report a Problem' | 'Confidential Report';

const TYPE_CONFIG: Record<SuggestionType, { icon: React.FC<{className?: string}>, title: string, desc: string, color: string }> = {
  'Suggestion': { icon: Lightbulb, title: 'Make a Suggestion', desc: 'Got an idea to improve things? We love hearing from you.', color: 'border-primary/30 bg-primary/5' },
  'Report a Problem': { icon: AlertTriangle, title: 'Report a Problem', desc: 'Something not working right? Let us know so we can fix it.', color: 'border-amber-500/30 bg-amber-500/5' },
  'Confidential Report': { icon: ShieldAlert, title: 'Confidential Report', desc: 'A safe, confidential channel for reporting serious concerns or whistleblowing. Your identity can be kept anonymous.', color: 'border-red-500/30 bg-red-500/5' },
};

export default function Suggestions() {
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState<SuggestionType>('Suggestion');
  const [form, setForm] = useState({ subject: '', content: '', anonymous: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const config = TYPE_CONFIG[selectedType];
  const Icon = config.icon;

  const handleSubmit = async () => {
    if (!form.subject.trim() || !form.content.trim()) { toast.error('Subject and content are required'); return; }
    setSubmitting(true);
    try {
      await submitSuggestion({ subject: form.subject, type: selectedType, content: form.content, anonymous: form.anonymous });
      setSubmitted(true);
    } catch (e: any) { toast.error(e.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Thank you!</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Your {selectedType.toLowerCase()} has been submitted{form.anonymous ? ' anonymously' : ''}. The management team has been notified and will review it.
          </p>
          <Button onClick={() => { setSubmitted(false); setForm({ subject: '', content: '', anonymous: false }); }}>Submit Another</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 py-6">
        <h1 className="text-xl font-bold text-foreground">Feedback & Reporting</h1>
        <p className="text-sm text-muted-foreground mt-1">Your voice matters. Share ideas, report problems, or raise concerns confidentially.</p>
      </div>

      <div className="px-6 py-6 max-w-2xl mx-auto space-y-5">
        {/* Type selector */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(Object.keys(TYPE_CONFIG) as SuggestionType[]).map(type => {
            const { icon: TIcon, title, color } = TYPE_CONFIG[type];
            const active = selectedType === type;
            return (
              <button key={type} onClick={() => setSelectedType(type)}
                className={`text-left rounded-xl border p-3 transition-all ${active ? color + ' border-opacity-100' : 'border-border hover:border-primary/30 bg-card'}`}>
                <TIcon className={`w-5 h-5 mb-1.5 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                <p className={`text-xs font-semibold ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{title}</p>
              </button>
            );
          })}
        </div>

        {/* Selected type info */}
        <div className={`rounded-xl border p-4 ${config.color}`}>
          <div className="flex items-start gap-3">
            <Icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">{config.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{config.desc}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="border border-border rounded-xl bg-card p-5 space-y-4">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Subject *</label>
            <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder={selectedType === 'Suggestion' ? 'e.g. Idea for better stock management' : 'Brief summary of the issue'} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Details *</label>
            <Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Please provide as much detail as helpful..." className="min-h-32" />
          </div>
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
            <Switch id="anon" checked={form.anonymous} onCheckedChange={v => setForm(f => ({ ...f, anonymous: v }))} />
            <div>
              <Label htmlFor="anon" className="text-sm font-medium text-foreground cursor-pointer">Submit anonymously</Label>
              <p className="text-xs text-muted-foreground">Your name will not be associated with this submission</p>
            </div>
          </div>
          {!form.anonymous && <p className="text-xs text-muted-foreground">Submitting as: {user?.firstName} {user?.lastName}</p>}
          <Button onClick={handleSubmit} disabled={submitting} className="w-full">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : `Submit ${selectedType}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
